/**
 * Hand-written validators for the two wire messages this SDK reads and writes.
 *
 * Why not reuse `@tryportal/contracts`? Because of where this code runs. A
 * hosted game is a static bundle served on the runtime origin under a CSP
 * compiled from its manifest, and the common case — `permissions.network.mode =
 * NONE` — produces `connect-src 'none'` and `script-src` without `unsafe-eval`.
 * A schema compiler that builds validators by generating functions cannot run
 * there, and a package that drags a JSON Schema engine into a game bundle costs
 * the game more than the check is worth.
 *
 * The risk this trades for is drift: a schema changes and these functions do
 * not. `test/validate.contract.test.ts` closes it by generating cases from the
 * real `contracts/schemas/*.json` and asserting that both validators agree on
 * every one, so a schema change that these functions miss fails the build rather
 * than reaching a player.
 *
 * These validators are total: they never throw, so a malformed frame can never
 * take down the game loop that is pumping them.
 */
const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/;
const HEX_64 = /^[a-f0-9]{64}$/;
const HEX_32 = /^[a-f0-9]{32}$/;
const ACTION_KEY = /^[a-z][a-z0-9_]{1,63}$/;
const SEMVER = /^\d+\.\d+\.\d+$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}[Tt]\d{2}:\d{2}:\d{2}(\.\d+)?([Zz]|[+-]\d{2}:\d{2})$/;
const ACTION_TARGETS = new Set(['GAME', 'OVERLAY', 'TTS', 'SOUND', 'GOAL']);
const SAFETY_CLASSES = new Set(['COSMETIC', 'GAMEPLAY', 'INTERRUPTIVE']);
const ACK_STATUSES = new Set(['APPLIED', 'REJECTED', 'EXPIRED', 'FAILED', 'DUPLICATE']);
const ACK_REASONS = new Set([
    'UNKNOWN_ACTION',
    'INVALID_PAYLOAD',
    'GAME_STATE_BLOCKED',
    'LOCAL_RATE_LIMIT',
    'TTL_EXPIRED',
    'INTERNAL_ERROR',
]);
const ENVELOPE_KEYS = new Set([
    'schemaVersion',
    'messageType',
    'actionId',
    'idempotencyKey',
    'liveSessionId',
    'gameSessionId',
    'ruleSetId',
    'ruleSetVersion',
    'sourceEventIds',
    'target',
    'actionKey',
    'safetyClass',
    'payload',
    'createdAt',
    'expiresAt',
    'attempt',
    'traceId',
]);
const ACK_KEYS = new Set([
    'schemaVersion',
    'messageType',
    'ackId',
    'actionId',
    'gameSessionId',
    'status',
    'reasonCode',
    'detail',
    'appliedAt',
    'receivedAt',
    'clientSequence',
    'sdkVersion',
]);
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isPlainObject(value) {
    return isRecord(value);
}
function isString(value, pattern) {
    return typeof value === 'string' && (pattern === undefined || pattern.test(value));
}
function isDateTime(value) {
    if (!isString(value, DATE_TIME))
        return false;
    return !Number.isNaN(Date.parse(value));
}
function isInteger(value, min, max) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
}
function hasOnlyKeys(value, allowed) {
    for (const key of Object.keys(value)) {
        if (!allowed.has(key))
            return false;
    }
    return true;
}
/**
 * Validate an inbound `ACTION` frame against `action-envelope.schema.json`.
 *
 * Step 1 of the required action algorithm. Nothing downstream reads a field off
 * a frame that has not passed this, which is why it returns a type predicate
 * rather than a boolean the caller might forget to branch on.
 */
export function isGameActionEnvelope(value) {
    if (!isRecord(value))
        return false;
    if (!hasOnlyKeys(value, ENVELOPE_KEYS))
        return false;
    if (value['schemaVersion'] !== 1)
        return false;
    if (value['messageType'] !== 'ACTION')
        return false;
    if (!isString(value['actionId'], ULID))
        return false;
    if (!isString(value['idempotencyKey'], HEX_64))
        return false;
    if (!isString(value['liveSessionId'], ULID))
        return false;
    if (!isString(value['ruleSetId'], ULID))
        return false;
    if (!isInteger(value['ruleSetVersion'], 1, Number.MAX_SAFE_INTEGER))
        return false;
    const target = value['target'];
    if (!isString(target) || !ACTION_TARGETS.has(target))
        return false;
    // The conditional half of the schema: a GAME action names a game session, and
    // every other target must not. This SDK only ever legitimately sees GAME
    // (invariant 12), but validating the whole rule keeps the two implementations
    // comparable in the contract test.
    const gameSessionId = value['gameSessionId'];
    if (target === 'GAME') {
        if (!isString(gameSessionId, ULID))
            return false;
    }
    else if (gameSessionId !== null) {
        return false;
    }
    const sourceEventIds = value['sourceEventIds'];
    if (!Array.isArray(sourceEventIds))
        return false;
    if (sourceEventIds.length < 1 || sourceEventIds.length > 100)
        return false;
    const seen = new Set();
    for (const id of sourceEventIds) {
        if (!isString(id, ULID))
            return false;
        if (seen.has(id))
            return false;
        seen.add(id);
    }
    if (!isString(value['actionKey'], ACTION_KEY))
        return false;
    const safetyClass = value['safetyClass'];
    if (!isString(safetyClass) || !SAFETY_CLASSES.has(safetyClass))
        return false;
    if (!isPlainObject(value['payload']))
        return false;
    if (!isDateTime(value['createdAt']))
        return false;
    if (!isDateTime(value['expiresAt']))
        return false;
    if (!isInteger(value['attempt'], 1, 3))
        return false;
    if ('traceId' in value && !isString(value['traceId'], HEX_32))
        return false;
    return true;
}
/**
 * Validate an outbound ACK against `action-ack.schema.json`.
 *
 * The SDK builds every ACK itself, so a failure here is a bug in this package
 * rather than a hostile input. Checking anyway is what turns that bug into a
 * caught test failure instead of a gateway-side protocol error mid-stream.
 */
export function isActionAck(value) {
    if (!isRecord(value))
        return false;
    if (!hasOnlyKeys(value, ACK_KEYS))
        return false;
    if (value['schemaVersion'] !== 1)
        return false;
    if (value['messageType'] !== 'ACTION_ACK')
        return false;
    if (!isString(value['ackId'], ULID))
        return false;
    if (!isString(value['actionId'], ULID))
        return false;
    if (!isString(value['gameSessionId'], ULID))
        return false;
    const status = value['status'];
    if (!isString(status) || !ACK_STATUSES.has(status))
        return false;
    if (!isDateTime(value['receivedAt']))
        return false;
    if ('reasonCode' in value) {
        const reasonCode = value['reasonCode'];
        if (reasonCode !== null && (!isString(reasonCode) || !ACK_REASONS.has(reasonCode))) {
            return false;
        }
    }
    if ('detail' in value) {
        const detail = value['detail'];
        if (detail !== null && (!isString(detail) || detail.length > 300))
            return false;
    }
    if ('appliedAt' in value) {
        const appliedAt = value['appliedAt'];
        if (appliedAt !== null && !isDateTime(appliedAt))
            return false;
    }
    if ('clientSequence' in value) {
        const clientSequence = value['clientSequence'];
        if (clientSequence !== null && !isInteger(clientSequence, 0, Number.MAX_SAFE_INTEGER)) {
            return false;
        }
    }
    if ('sdkVersion' in value && !isString(value['sdkVersion'], SEMVER))
        return false;
    return true;
}
/** Exported for the contract test, which needs the same notion of "well formed". */
export const PATTERNS = { ULID, HEX_64, HEX_32, ACTION_KEY, SEMVER, DATE_TIME };
